// src/components/personal/sections/ClientsSection/error/error-handlers.ts
import React from 'react';
import { APIError } from '../types/types';
import { ERROR_CODES } from '../error/error-constants';
import { ErrorLogger } from '@/utils/error-logger';
// Импортируем только тип сообщения, а не сам компонент
import MessageComponent from '@/components/ui/Message';

/**
 * Типы ошибок API
 */
export type ApiErrorType = 
  | 'network'
  | 'validation'
  | 'not_found'
  | 'auth'
  | 'server'
  | 'unknown';

// Маппинг кодов ошибок на ключи переводов
const ERROR_TRANSLATION_KEYS: Record<string, string> = {
  'NETWORK_ERROR': 'errors.network',
  'VALIDATION_ERROR': 'errors.validation',
  'NOT_FOUND': 'errors.not_found',
  'UNAUTHORIZED': 'errors.unauthorized',
  'FORBIDDEN': 'errors.forbidden',
  'SERVER_ERROR': 'errors.server',
  'UNKNOWN_ERROR': 'errors.unknown',
  'UPDATE_SETTINGS_FAILED': 'errors.update_settings_failed'
};

/**
 * Отображение уведомления об ошибке
 */
export const displayErrorNotification = (error: APIError): void => {
  // Получаем ключ перевода для кода ошибки (для использования в реальной интеграции)
  // const translateKey = ERROR_TRANSLATION_KEYS[error.code] || 'errors.unknown';
  
  // Здесь можно интегрировать с UI компонентом Message из темы
  // Временно используем базовый консольный лог
  console.error(`Error (${error.code}):`, error.message);
  
  // ДЛЯ ПРИМЕРА: Для реального использования в приложении
  // Закомментировано, чтобы не вызывать ошибки компиляции
  /*
  // Используя React и toast
  import { toast } from 'react-toastify';
  
  // Простой вариант
  toast.error(error.message, {
    position: "top-right",
    autoClose: 5000
  });
  
  // Или если нужен комплексный компонент:
  const translateKey = ERROR_TRANSLATION_KEYS[error.code] || 'errors.unknown';
  toast(() => (
    <MessageComponent 
      type="error"
      translateKey={translateKey}
      message={error.message}
    />
  ));
  */
};

/**
 * Проверка на сетевую ошибку
 */
export const isNetworkError = (error: APIError): boolean => {
  return error.code === 'NETWORK_ERROR';
};

/**
 * Проверка на ошибку валидации
 */
export const isValidationError = (error: APIError): boolean => {
  return error.code === 'VALIDATION_ERROR';
};

/**
 * Получить тип ошибки по коду
 */
export const getErrorType = (error: APIError): ApiErrorType => {
  switch (error.code) {
    case ERROR_CODES.NETWORK_ERROR:
      return 'network';
    case ERROR_CODES.VALIDATION_ERROR:
      return 'validation';
    case ERROR_CODES.NOT_FOUND:
      return 'not_found';
    case ERROR_CODES.UNAUTHORIZED:
    case ERROR_CODES.FORBIDDEN:
      return 'auth';
    case ERROR_CODES.SERVER_ERROR:
      return 'server';
    default:
      return 'unknown';
  }
};

/**
 * Проверить, нужно ли повторить запрос
 */
export const shouldRetry = (error: APIError): boolean => {
  const errorType = getErrorType(error);
  return errorType === 'network' || errorType === 'server';
};

/**
 * Получить ключ перевода для кода ошибки
 */
export const getErrorTranslationKey = (errorCode: string): string => {
  return ERROR_TRANSLATION_KEYS[errorCode] || 'errors.unknown';
};

// Правильное объявление интерфейса с использованием React.FC
interface ErrorDisplayProps {
  error: APIError | null;
  className?: string;
}

/**
 * Компонент для отображения ошибки внутри компонентов React
 */
export const ErrorDisplay: React.FC<ErrorDisplayProps> = ({ error, className }) => {
  if (!error) return null;
  
  const errorTranslateKey = getErrorTranslationKey(error.code);
  
  // Используем импортированный компонент MessageComponent
  return React.createElement(MessageComponent, {
    type: "error",
    message: error.message,
    translateKey: errorTranslateKey,
    className: className
  });
};

/**
 * Комплексная обработка ошибки API
 */
export const handleApiError = async (
  error: APIError, 
  source: string,
  showNotification: boolean = true
): Promise<ApiErrorType> => {
  // Логирование ошибки
  await ErrorLogger.logError(error, source);
  
  // Определение типа ошибки
  const errorType = getErrorType(error);
  
  // Уведомление пользователя (если нужно)
  if (showNotification) {
    displayErrorNotification(error);
  }
  
  // Обработка в зависимости от типа ошибки
  switch (errorType) {
    case 'auth':
      // Перенаправление на страницу входа
      // Проверяем окружение браузера безопасным способом
      if (typeof document !== 'undefined') {
        // В браузере делаем редирект
        const currentPath = (document as any).location.pathname;
        if (currentPath !== '/login') {
          (document as any).location.href = '/login';
        }
      }
      break;
  }
  
  return errorType;
};